import { useEffect, useState, useRef } from 'react';
import { Video, Pin, Flame, Eye, Swords, Trash2, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import ArenaxCreatorBadgeStrip from './ArenaxCreatorBadgeStrip';

/**
 * Phase 2O PR 2 — Match Clip Reel.
 *
 * Vertical-first highlight reel for a live (or completed) match. Renders inside
 * the spectator room. Each clip:
 *  - autoplay-on-hover (muted) — TikTok-style discovery
 *  - inherits narrative context in the caption ("Sai's winner against Titans —
 *    extending Strikers' unbeaten rivalry streak to 5")
 *  - pinned clips appear first (memory anchors)
 *  - reactions: 🔥 / 👀 / ⚔️
 */
const REACTIONS = ['🔥', '⚔️', '👀', '🏆'];

const BADGE_META = {
  title_moment:    { label: 'TITLE MOMENT',    Icon: () => <span>🏆</span>, tone: 'bg-amber-400 text-slate-950 ring-amber-300/50' },
  rivalry_classic: { label: 'RIVALRY CLASSIC', Icon: () => <span>⚔️</span>, tone: 'bg-rose-500 text-slate-50 ring-rose-400/50' },
  crew_moment:     { label: 'CREW MOMENT',     Icon: () => <span>🔥</span>, tone: 'bg-orange-500 text-slate-50 ring-orange-400/50' },
};

export default function ArenaxClipReel({ matchId, canUpload = false }) {
  const { user } = useAuth();
  const [clips, setClips] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    if (!matchId) return;
    api.get(`/arenax/matches/${matchId}/clips?limit=24`)
      .then(r => setClips(r.data?.clips || []))
      .catch(() => setClips([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // Light auto-refresh so new clips surface without reload
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  if (loading && clips.length === 0 && !canUpload) return null;

  return (
    <section className="mt-5" data-testid="arenax-clip-reel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Video className="h-4 w-4 text-cyan-300" />
          <span className="text-[10px] tracking-[0.3em] text-cyan-300 font-black uppercase">Clip Reel</span>
          {clips.length > 0 && <span className="text-[10px] text-slate-500">{clips.length}</span>}
        </div>
        {canUpload && (
          <button
            data-testid="arenax-clip-upload-open"
            onClick={() => setUploadOpen(true)}
            className="px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider
                          text-slate-950 bg-gradient-to-r from-cyan-300 to-amber-300
                          hover:scale-[1.03] transition-transform inline-flex items-center gap-1.5">
            <Upload className="h-3.5 w-3.5" />
            Add clip
          </button>
        )}
      </div>

      {clips.length === 0 ? (
        <div className="rounded-xl border border-slate-800 px-4 py-5 text-center bg-slate-900/30"
          data-testid="arenax-clip-reel-empty">
          <p className="text-sm text-slate-400">
            No clips yet. {canUpload ? "Be the first to capture a memory anchor." : "The crew on the ground will start dropping highlights any minute."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {clips.map(clip => (
            <ClipCard key={clip.id} clip={clip} canPin={user?.role === 'player'}
              onChanged={reload} />
          ))}
        </div>
      )}

      {uploadOpen && (
        <ClipUploadModal matchId={matchId} onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); reload(); }} />
      )}
    </section>
  );
}

function ClipCard({ clip, canPin, onChanged }) {
  const videoRef = useRef(null);
  const [reactingTo, setReactingTo] = useState(null);
  const streamUrl = `${process.env.REACT_APP_BACKEND_URL}/api/arenax/clips/${clip.id}/stream`;

  const onMouseEnter = () => {
    try { videoRef.current?.play(); } catch (_e) { /* no-op */ }
  };
  const onMouseLeave = () => {
    try { videoRef.current?.pause(); videoRef.current.currentTime = 0; } catch (_e) { /* no-op */ }
  };

  const react = async (emoji) => {
    setReactingTo(emoji);
    try {
      await api.post(`/arenax/clips/${clip.id}/react`, { emoji });
      onChanged?.();
    } catch (_e) { /* silent */ }
    finally { setReactingTo(null); }
  };

  const togglePin = async () => {
    try {
      const r = await api.post(`/arenax/clips/${clip.id}/pin`);
      toast.success(r.data?.clip?.pinned ? 'Pinned to the reel.' : 'Unpinned.');
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not pin clip.';
      toast.error(detail);
    }
  };

  return (
    <div className={`relative rounded-xl overflow-hidden border bg-slate-900
                        ${clip.pinned ? 'border-amber-500/40 ring-1 ring-amber-500/20' : 'border-slate-800'}`}
      data-testid={`arenax-clip-card-${clip.id}`}
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {/* Vertical-first 9:16 frame */}
      <div className="relative aspect-[9/16] bg-slate-950">
        <video ref={videoRef} src={streamUrl} preload="metadata" muted playsInline loop
          data-testid={`arenax-clip-video-${clip.id}`}
          className="absolute inset-0 w-full h-full object-cover" />
        {clip.auto_pinned && clip.badge && BADGE_META[clip.badge] && (
          <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-[9px] font-black uppercase
                            tracking-wider flex items-center gap-1 ring-2 ${BADGE_META[clip.badge].tone}`}
            data-testid={`arenax-clip-badge-${clip.badge}-${clip.id}`}>
            {BADGE_META[clip.badge].Icon()} {BADGE_META[clip.badge].label}
          </div>
        )}
        {!clip.auto_pinned && clip.pinned && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-amber-500/95 text-slate-950
                            text-[9px] font-black uppercase tracking-wider flex items-center gap-1"
            data-testid={`arenax-clip-pinned-badge-${clip.id}`}>
            <Pin className="h-2.5 w-2.5" /> Pinned
          </div>
        )}
        {clip.view_count > 0 && (
          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-slate-950/70 text-slate-300
                            text-[9px] font-mono flex items-center gap-1">
            <Eye className="h-2.5 w-2.5" /> {clip.view_count}
          </div>
        )}
      </div>
      {/* Caption */}
      <div className="px-3 py-2.5 space-y-2">
        <p className="text-[11px] text-slate-200 leading-snug line-clamp-3"
          data-testid={`arenax-clip-caption-${clip.id}`}>
          {clip.caption_narrative}
        </p>
        {/* Phase 2R-Creator — uploader byline + earned badges (quiet) */}
        {clip.uploader_id && (
          <ArenaxCreatorBadgeStrip
            uploaderId={clip.uploader_id}
            uploaderName={clip.uploader_name}
          />
        )}
        {/* Reactions strip */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {REACTIONS.map(emoji => (
              <button key={emoji} onClick={() => react(emoji)} disabled={reactingTo === emoji}
                data-testid={`arenax-clip-react-${clip.id}-${emoji}`}
                className="px-1.5 py-0.5 rounded-md bg-slate-800/60 hover:bg-slate-700 text-[10px]
                              transition-colors disabled:opacity-50 inline-flex items-center gap-1">
                <span>{emoji}</span>
                <span className="text-slate-400 font-mono">{clip.reactions?.[emoji] || 0}</span>
              </button>
            ))}
          </div>
          {canPin && (
            <button onClick={togglePin}
              data-testid={`arenax-clip-pin-toggle-${clip.id}`}
              className="p-1.5 rounded-md hover:bg-slate-800 transition-colors">
              <Pin className={`h-3 w-3 ${clip.pinned ? 'text-amber-300' : 'text-slate-500'}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ClipUploadModal({ matchId, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [scorer, setScorer] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error('Clip must be under 50 MB.');
      return;
    }
    if (!f.type.startsWith('video/')) {
      toast.error('Please pick a video file.');
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (scorer) fd.append('scorer_name', scorer);
      if (caption) fd.append('caption', caption);
      await api.post(`/arenax/matches/${matchId}/clips`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Clip uploaded.');
      onUploaded?.();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Upload failed.';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur grid place-items-center px-4"
      data-testid="arenax-clip-upload-modal">
      <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-slate-100">Capture a memory</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800">
            <X className="h-4 w-4 text-slate-400" />
          </button>
        </div>
        <p className="text-sm text-slate-400 mb-4 leading-snug">
          Drop a short vertical clip. We'll write the caption automatically using the match's narrative context.
        </p>
        {/* File picker */}
        <input ref={fileInputRef} type="file" accept="video/*"
          data-testid="arenax-clip-upload-file" className="hidden" onChange={onPick} />
        <button onClick={() => fileInputRef.current?.click()}
          data-testid="arenax-clip-upload-pick"
          className="w-full px-4 py-8 rounded-xl border-2 border-dashed border-slate-700
                       hover:border-cyan-500/60 transition-colors text-center">
          {file ? (
            <div>
              <Video className="h-5 w-5 text-cyan-300 mx-auto mb-2" />
              <div className="text-sm text-slate-200 font-medium truncate">{file.name}</div>
              <div className="text-[11px] text-slate-500 mt-1">
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </div>
            </div>
          ) : (
            <div>
              <Upload className="h-5 w-5 text-slate-500 mx-auto mb-2" />
              <div className="text-sm text-slate-400">Pick a video (≤ 50 MB)</div>
              <div className="text-[10px] text-slate-600 mt-1 tracking-wider">MP4 · WEBM · MOV</div>
            </div>
          )}
        </button>
        {/* Optional context fields */}
        <div className="mt-4 space-y-2.5">
          <input value={scorer} onChange={e => setScorer(e.target.value.slice(0, 40))}
            data-testid="arenax-clip-upload-scorer"
            placeholder="Scorer (optional, e.g. 'Sai')"
            className="w-full px-3 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700
                         text-slate-100 text-sm placeholder-slate-500" />
          <input value={caption} onChange={e => setCaption(e.target.value.slice(0, 120))}
            data-testid="arenax-clip-upload-caption"
            placeholder="Fallback caption (used if no narrative context)"
            className="w-full px-3 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700
                         text-slate-100 text-sm placeholder-slate-500" />
        </div>
        {/* Submit */}
        <div className="mt-4 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-700
                                                    text-slate-300 text-sm font-bold hover:bg-slate-800">
            Cancel
          </button>
          <button onClick={submit} disabled={!file || uploading}
            data-testid="arenax-clip-upload-submit"
            className="flex-1 px-4 py-2.5 rounded-xl font-black text-sm text-slate-950
                         bg-gradient-to-r from-cyan-300 to-amber-300 hover:scale-[1.02]
                         transition-transform disabled:opacity-50 disabled:cursor-not-allowed">
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
